// SelfieCapture — Step 2 of the Identity Gate wizard.
//
// Captures a live front-camera selfie and POSTs it to /api/identity/upload-selfie.
// Simpler than IdUpload — no file-pick option, the selfie MUST come from
// the actual webcam (we can't trust a file the candidate hands us, that
// could be the same image as the ID and trivially defeat the match).
//
// AWS Face Liveness (anti-spoof) would be the proper way to prevent
// "candidate holds up a photo of someone else" but that requires the
// Amplify Liveness React component (extra npm dep). When the
// `identity.liveness_check` feature flag is ON, this component should
// be replaced by a Liveness flow — for v1 we render this with a clear
// note that liveness is "best-effort".

import React, { useState } from 'react';
import {
  CheckCircle2, AlertTriangle, RefreshCw,
} from 'lucide-react';
import WebcamCapture from './WebcamCapture';

export default function SelfieCapture({ onUpload, busy }) {
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handleCapture = async (blob) => {
    setError(null);
    const res = await onUpload(blob);
    if (res?.ok) {
      setDone(true);
    } else {
      setError(res?.error || 'Selfie upload failed');
    }
  };

  const retake = () => {
    setDone(false);
    setError(null);
  };

  if (done) {
    return (
      <div className="space-y-4">
        <Heading>Selfie captured</Heading>
        <div className="flex items-start gap-3 p-4 rounded-lg border bg-green-50 border-green-200">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-green-900">
              Selfie uploaded successfully
            </div>
            <div className="text-green-700 mt-0.5">
              Click Verify identity below to compare your selfie against
              the ID photo.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={retake}
          disabled={busy}
          className="inline-flex items-center text-sm text-blue-700 hover:text-blue-900"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Retake selfie
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Heading>Take a live selfie</Heading>
      <p className="text-sm text-gray-600">
        Look directly into the camera. Make sure your face is clearly lit,
        not covered, and facing forward. Remove sunglasses or masks.
      </p>
      <WebcamCapture
        onCapture={handleCapture}
        facingMode="user"
        mirror
        width={480}
        height={360}
        ctaLabel="Take selfie"
      />
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function Heading({ children }) {
  return <h3 className="text-lg font-semibold text-gray-900">{children}</h3>;
}
